import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit as firestoreLimit,
  setDoc,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import {
  UserSearchIndex,
  MentionSearchResult,
  MentionSearchContext,
} from '../types/mention';

/**
 * MentionSearchService
 * Handles user search for mention autocomplete with Firestore indexing
 *
 * This service provides:
 * - Fast user search via indexed search tokens
 * - Permission-aware filtering
 * - Search index maintenance
 */
export class MentionSearchService {
  private static readonly SEARCH_INDEX_COLLECTION = 'userSearchIndex';

  /**
   * Search users for mention autocomplete
   * Uses Firestore array-contains query on searchTokens
   *
   * @param searchTerm - Partial name/email (min 1 char)
   * @param context - Search context for permission filtering
   * @param limitCount - Max results (default 10)
   * @returns Array of matching users
   */
  static async searchUsersForMention(
    searchTerm: string,
    context: MentionSearchContext,
    limitCount: number = 10
  ): Promise<MentionSearchResult[]> {
    try {
      // Normalize search term
      const term = searchTerm.toLowerCase().trim();

      if (term.length === 0) {
        return [];
      }

      // Query Firestore using array-contains on searchTokens
      // This works because we tokenize names like "John Doe" into ["john", "doe", "john.doe"]
      const indexRef = collection(db, this.SEARCH_INDEX_COLLECTION);
      const q = query(
        indexRef,
        where('isActive', '==', true),
        where('isApproved', '==', true),  // Only show approved users
        where('searchTokens', 'array-contains', term),
        firestoreLimit(limitCount * 2) // Get extra for client-side filtering
      );

      const snapshot = await getDocs(q);

      // Convert to search results
      let results: MentionSearchResult[] = snapshot.docs.map(doc => {
        const data = doc.data() as UserSearchIndex;
        return {
          userId: doc.id, // Use document ID as userId (userId is not stored in the document)
          displayName: data.displayName,
          email: data.email,
          personName: data.personName,
          personId: data.personId,
          photoURL: data.photoURL,
        };
      });

      // Client-side filtering for better ranking
      results = results.filter(user => {
        const nameLower = user.displayName.toLowerCase();
        const emailLower = user.email.toLowerCase();
        const personNameLower = user.personName?.toLowerCase() || '';

        return nameLower.includes(term) ||
               emailLower.includes(term) ||
               personNameLower.includes(term);
      });

      // Sort by relevance (exact prefix matches first)
      results.sort((a, b) => {
        const aNameLower = a.displayName.toLowerCase();
        const bNameLower = b.displayName.toLowerCase();

        const aStartsWith = aNameLower.startsWith(term);
        const bStartsWith = bNameLower.startsWith(term);

        if (aStartsWith && !bStartsWith) return -1;
        if (!aStartsWith && bStartsWith) return 1;

        return aNameLower.localeCompare(bNameLower);
      });

      // Apply permission filtering based on context
      results = await this.filterByPermissions(results, context);

      // Return limited results
      return results.slice(0, limitCount);
    } catch (error) {
      console.error('Error searching users for mention:', error);
      return [];
    }
  }

  /**
   * Filter search results by permission context
   * Different content types have different visibility rules
   */
  private static async filterByPermissions(
    results: MentionSearchResult[],
    context: MentionSearchContext
  ): Promise<MentionSearchResult[]> {
    // For Phase 1, we implement simple filtering
    // In production, you'd check actual permissions based on contentType

    switch (context.contentType) {
      case 'comment':
        // Comments: Any authenticated family member
        return results;

      case 'message':
        // Messages: Would need to check conversation participants
        // For now, return all (will be implemented in Phase 4)
        return results;

      case 'note':
        // Notes: Depends on note visibility (private vs public)
        // For now, return all (will be implemented in Phase 5)
        return results;

      case 'memory':
        // Memories: Depends on memory visibility
        // For now, return all (will be implemented in Phase 5)
        return results;

      case 'feature_request':
        // Feature requests: All users + developers
        return results;

      default:
        return results;
    }
  }

  /**
   * Index a user for search
   * Called when user is created or updated
   *
   * @param userId - User ID to index
   * @param displayName - User's display name
   * @param email - User's email
   * @param isApproved - Whether user is approved to appear in search
   * @param personId - Optional linked person ID
   * @param personName - Optional linked person name
   * @param photoURL - Optional photo URL
   */
  static async indexUser(
    userId: string,
    displayName: string,
    email: string,
    isApproved: boolean = false,
    personId?: string,
    personName?: string,
    photoURL?: string
  ): Promise<void> {
    try {
      // Generate search tokens
      const searchTokens = this.generateSearchTokens(displayName, email, personName);

      // Build index data with required fields
      const indexData: Partial<UserSearchIndex> = {
        displayName,
        displayNameLower: displayName.toLowerCase(),
        email,
        emailLower: email.toLowerCase(),
        searchTokens,
        isActive: true,
        isApproved,
        updatedAt: serverTimestamp() as Timestamp,
      };

      // Add optional fields only if they exist
      if (personId) {
        indexData.personId = personId;
      }
      if (personName) {
        indexData.personName = personName;
        indexData.personNameLower = personName.toLowerCase();
      }
      if (photoURL) {
        indexData.photoURL = photoURL;
      }

      // Remove undefined fields before writing to Firestore
      const cleanedData = this.removeUndefinedFields(indexData);

      // Write to Firestore
      await setDoc(
        doc(db, this.SEARCH_INDEX_COLLECTION, userId),
        cleanedData
      );
    } catch (error) {
      console.error('Error indexing user:', error);
      // Don't throw - indexing is non-critical
    }
  }

  /**
   * Remove undefined fields from an object (Firestore doesn't accept undefined)
   * Recursively cleans nested objects and arrays
   */
  private static removeUndefinedFields(obj: any): any {
    if (obj === null || obj === undefined) {
      return obj;
    }

    // Handle arrays
    if (Array.isArray(obj)) {
      return obj
        .filter(item => item !== undefined)
        .map(item => this.removeUndefinedFields(item));
    }

    // Handle objects
    if (typeof obj === 'object' && !(obj instanceof Date)) {
      const result: any = {};
      for (const key in obj) {
        if (obj[key] !== undefined) {
          result[key] = this.removeUndefinedFields(obj[key]);
        }
      }
      return result;
    }

    // Return primitive values as-is
    return obj;
  }

  /**
   * Generate search tokens from name and email
   * Tokenizes: "John Doe" -> ["john", "doe", "john.doe", "j", "jo", "joh", "john", "d", "do", "doe"]
   *           "john.doe@email.com" -> ["john", "doe", "john.doe", "johndoe", full email, etc.]
   */
  private static generateSearchTokens(
    displayName: string,
    email: string,
    personName?: string
  ): string[] {
    const tokens = new Set<string>();

    // Tokenize display name
    const nameParts = displayName.toLowerCase().split(/\s+/);
    nameParts.forEach(part => {
      if (part.length > 0) {
        tokens.add(part);
        // Add progressive substrings for prefix matching
        for (let i = 1; i <= part.length; i++) {
          tokens.add(part.substring(0, i));
        }
      }
    });

    // Add full name as token
    tokens.add(nameParts.join('.'));
    tokens.add(nameParts.join(''));

    // Add full email for exact matching
    const emailLower = email.toLowerCase();
    tokens.add(emailLower);

    // Add email with progressive substrings for prefix search
    for (let i = 1; i <= emailLower.length; i++) {
      tokens.add(emailLower.substring(0, i));
    }

    // Tokenize email (before @)
    const emailPart = email.split('@')[0].toLowerCase();
    tokens.add(emailPart);

    // Add progressive substrings for email local part
    for (let i = 1; i <= emailPart.length; i++) {
      tokens.add(emailPart.substring(0, i));
    }

    const emailParts = emailPart.split(/[._-]/);
    emailParts.forEach(part => {
      if (part.length > 0) {
        tokens.add(part);
        for (let i = 1; i <= part.length; i++) {
          tokens.add(part.substring(0, i));
        }
      }
    });

    // Tokenize person name if available
    if (personName) {
      const personParts = personName.toLowerCase().split(/\s+/);
      personParts.forEach(part => {
        if (part.length > 0) {
          tokens.add(part);
          for (let i = 1; i <= part.length; i++) {
            tokens.add(part.substring(0, i));
          }
        }
      });
    }

    return Array.from(tokens);
  }

  /**
   * Rebuild entire search index (admin utility)
   * Should be run as a one-time migration or admin action
   *
   * This fetches all users from the 'users' collection and rebuilds their search index
   * with the enhanced token generation including full email support
   */
  static async rebuildSearchIndex(): Promise<{ success: number; failed: number }> {
    console.log('Starting search index rebuild...');
    let success = 0;
    let failed = 0;

    try {
      // Fetch all users from the users collection
      const usersRef = collection(db, 'users');
      const snapshot = await getDocs(usersRef);

      console.log(`Found ${snapshot.size} users to index`);

      // Process each user
      for (const userDoc of snapshot.docs) {
        try {
          const userData = userDoc.data();
          const userId = userDoc.id;

          // Extract required fields
          const displayName = userData.displayName || userData.email || 'Unknown User';
          const email = userData.email || '';
          const isApproved = userData.isApproved ?? false;
          const photoURL = userData.photoURL;

          // Get linked person info if available
          let personId: string | undefined;
          let personName: string | undefined;

          if (userData.personId) {
            personId = userData.personId;
            // Try to fetch person name from genealogy collection
            // Note: This may fail due to permissions, which is okay
            try {
              const personDoc = await getDoc(doc(db, 'people', userData.personId));
              if (personDoc.exists()) {
                const personData = personDoc.data();
                personName = personData.name || `${personData.firstName || ''} ${personData.lastName || ''}`.trim();
              }
            } catch (err) {
              // Silently skip if permissions denied - user search will still work without person name
              // Only log non-permission errors
              if (!(err instanceof Error && err.message.includes('permission'))) {
                console.warn(`Could not fetch person data for user ${userId}:`, err);
              }
            }
          }

          // Index the user
          await this.indexUser(
            userId,
            displayName,
            email,
            isApproved,
            personId,
            personName,
            photoURL
          );

          success++;

          // Log progress every 10 users
          if (success % 10 === 0) {
            console.log(`Indexed ${success}/${snapshot.size} users...`);
          }
        } catch (error) {
          console.error(`Failed to index user ${userDoc.id}:`, error);
          failed++;
        }
      }

      console.log(`Search index rebuild complete: ${success} succeeded, ${failed} failed`);
      return { success, failed };
    } catch (error) {
      console.error('Error rebuilding search index:', error);
      throw new Error(`Failed to rebuild search index: ${error}`);
    }
  }
}

export const mentionSearchService = MentionSearchService;
